import React, { useState, useEffect } from 'react';
import { View } from 'react-native';
import { useSelector } from 'react-redux';
import { SparcKpiListSection } from '../SparcKpiListSection';
import { LighthouseExecutionSection } from '../LighthouseExecutionSection';
import { createApiClient, apiClient } from '../../../../../api/apiClient';
import logger from '../../../../../utils/logger';
import { handleStandUpErrorWithFriendlyMessage } from '../../../../../store/utils/standUpErrorHandler';
import { STANDUP_API_NAMES } from '../../../../../store/slices/standUpSlice';
import { useSparcKpiFavorite } from '../../../hooks/useSparcKpiFavorite';
import { RootState } from '../../../../../store';

const mandatoryKpiSyncRegistry = new Set<string>();

interface ReviewPerformanceTabProps {
  favoriteSparcCards: string[];
  favoriteLighthouseCards: string[];
  onToggleSparcFavorite: (cardId: string) => void;
  onToggleLighthouseFavorite: (cardId: string) => void;
  selectedPeriod: string;
  onPeriodChange?: (period: string) => void;
  lighthouseCardsData?: Array<{
    id: string;
    title: string;
    question_id?: number;
    is_parent?: boolean;
    cw: number;
    lcw: number;
    lcp: number;
    ytd: number;
    chartData: Array<{ date: string; value: number }>;
  }>;
  standupType: string;
  standupId?: string;
  selectedLocation: { id: string; displayText: string; } | null;
  isLoadingLighthouse?: boolean;
  onAllCardsChange?: (cards: any[]) => void;
  onLighthouseCardActionPlan?: (card: any) => void;
  onSparcCardActionPlan?: (card: any) => void;
  metricTrendData?: any;
}

export const ReviewPerformanceTab: React.FC<ReviewPerformanceTabProps> = ({
  favoriteSparcCards,
  favoriteLighthouseCards,
  onToggleSparcFavorite,
  onToggleLighthouseFavorite,
  selectedPeriod,
  onPeriodChange,
  lighthouseCardsData = [],
  standupType,
  standupId,
  selectedLocation,
  isLoadingLighthouse = false,
  onAllCardsChange,
  onLighthouseCardActionPlan,
  onSparcCardActionPlan,
  metricTrendData,
}) => {
  const [sparcCardsData, setSparcCardsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [metricsType, setMetricsType] = useState('Daily');
  const pendingMandatorySyncRef = React.useRef<Set<string>>(new Set());
  const favoriteSparcCardsRef = React.useRef(favoriteSparcCards);
  const onToggleSparcFavoriteRef = React.useRef(onToggleSparcFavorite);
  const { toggleFavorite, refreshFavoritesList } = useSparcKpiFavorite();
  const favoritesFromRedux = useSelector((state: RootState) => state.standupRedirect?.favorites?.data);
  const favoritesLoading = useSelector((state: RootState) => state.standupRedirect?.favorites?.loading);

  useEffect(() => {
    favoriteSparcCardsRef.current = favoriteSparcCards;
  }, [favoriteSparcCards]);

  useEffect(() => {
    onToggleSparcFavoriteRef.current = onToggleSparcFavorite;
  }, [onToggleSparcFavorite]);

  // Helper function to transform standupType for API
  const transformStandupType = (type: string): string => {
    const standupTypeMap: { [key: string]: string } = {
      'daily-lt': 'daily_lt',
      'weekly-lt': 'weekly_lt',
      'daily-frontline': 'daily_flt',
      'daily_flt': 'daily_flt',
    };
    return standupTypeMap[type.toLowerCase()] || type;
  };

  // Helper function to map category to badge letter
  const getCategoryBadge = (category: string): string => {
    const categoryMap: { [key: string]: string } = {
      'Safety': 'S',
      'Accuracy': 'A',
      'Reliability': 'R',
      'People': 'P',
      'Cost': 'C',
    };
    return categoryMap[category] || 'S';
  };

  // Helper function to map category to badge color
  const getCategoryColor = (category: string): string => {
    const colorMap: { [key: string]: string } = {
      'Safety': '#D4F4DD',
      'Accuracy': '#FFF4D4',
      'Reliability': '#D4E4FF',
      'People': '#E3F2FD',
      'Cost': '#FDD4D4',
    };
    return colorMap[category] || '#D4F4DD';
  };

  // Helper function to check if KPI is percentage-based and needs value multiplication
  const isPercentageKpi = (kpiName: string): boolean => {
    const percentageKpis = [
      'BPA',
      'Bulk Pallet Accuracy',
      'Build Location Fill Rate',
      'DPS',
      'HAULBACK',
      'In House Cut',
      'KU',
      'MLP',
      'On Time',
      'Over Time',
      'Self Inflicted Audit',
      'Turn Over',
      'REPACK',
      'PTE',
      'Perfect Truck Execution'
    ];
    const normalizedKpiName = kpiName.toLowerCase().replace(/[%\s]/g, '');
    return percentageKpis.some(pKpi => {
      const normalizedPKpi = pKpi.toLowerCase().replace(/[%\s]/g, '');
      return normalizedKpiName.includes(normalizedPKpi) || 
             normalizedPKpi.includes(normalizedKpiName) ||
             normalizedKpiName === normalizedPKpi;
    });
  };

  // Helper function to check if KPI should use inverted delta color semantics
  const isInvertedDeltaColorKpi = (kpiName: string): boolean => {
    const invertedDeltaColorKpis = [
      'ALL-IN-WASTE $',
      'HAULBACK %',
      'In House Cut %',
      'Injuries',
      'Overtime %',
      'Self Inflicted Audit %',
      '$ Direct Labor Cost / Cases',
      'Recordable Injury Rate (RIR)',
      'SIA%',
      'Turn Over %',
      'Time to Punch',
      'WH WH Breakage %',
      'Rapid Break $',
    ];
    const normalizedKpiName = kpiName.toLowerCase().replace(/[%\s]/g, '');
    return invertedDeltaColorKpis.some(rKpi => {
      const normalizedRKpi = rKpi.toLowerCase().replace(/[%\s]/g, '');
      return normalizedKpiName.includes(normalizedRKpi) || 
             normalizedRKpi.includes(normalizedKpiName) ||
             normalizedKpiName === normalizedRKpi;
    });
  };

  // Helper function to format a backend date string (YYYY-MM-DD) for UI display as M/D.
  // Parses the string directly instead of using `new Date()` to avoid local timezone conversion.
  const formatChartDate = (dateString: string): string => {
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      return `${month}/${day}`;
    }
    return dateString;
  };

  // Normalizes the Gap Time KPI title regardless of the suffix the backend returns
  const formatKpiTitle = (kpiName: string): string => {
    return kpiName?.trim().toUpperCase().startsWith('GAP TIME')
      ? 'Gap Time (Avg Min / Operator)'
      : kpiName;
  };

  // Fetch KPI metrics from API
  const fetchKpiMetrics = async () => {
    if (!selectedLocation) return;

    setIsLoading(true);
    try {
      const api = createApiClient();
      const transformedStandupType = transformStandupType(standupType);
      const response = await apiClient(
        api,
        `/stand-up/kpi-metrics-v2?location=${encodeURIComponent(selectedLocation.displayText)}&standUpType=${transformedStandupType}&metricsType=${metricsType.toLowerCase()}${standupId ? `&standup_id=${encodeURIComponent(standupId)}` : ''}`,
        'GET'
      );

      if (response?.data?.metrics) {
        // Transform API response to match SparcKpiCard format
        const transformedCards = response.data.metrics.map((metric: any) => {
          // Check if this KPI is percentage-based
          const isPercentage = isPercentageKpi(metric.kpi);
          const multiplier = isPercentage ? 100 : 1;

          // Transform only the latest 7 cadence points to chartData format
          const latestCadences = Array.isArray(metric.cadences_value)
            ? metric.cadences_value.slice(-7)
            : [];
          const chartData = latestCadences.map((cadence: any) => {
            const [dateKey, value] = Object.entries(cadence)[0];
            return {
              date: formatChartDate(dateKey),
              value: value == null ? null : (value as number) * multiplier,
            };
          });

          const actualValue = metric.actual_value == null ? null : metric.actual_value * multiplier;
          const targetValue = metric.target == null ? null : metric.target * multiplier;

          // Generic delta calculation for all KPIs
          const delta = actualValue == null || targetValue == null ? null : actualValue - targetValue;
          const invertDeltaColor = metric.kpi_code === 'gap_time' || isInvertedDeltaColorKpi(metric.kpi);

          return {
            id: `${metric.kpi_code || metric.kpi_id}`,
            kpiCode: `${metric.kpi_code || ''}`,
            kpiId: `${metric.kpi_id}`,
            title: isPercentage && !metric.kpi.includes('%') ? `${metric.kpi} %` : metric.kpi,
            category: metric.category,
            badgeLetter: getCategoryBadge(metric.category),
            badgeColor: getCategoryColor(metric.category),
            actual: actualValue,
            target: targetValue,
            delta: delta,
            invertDeltaColor,
            isMandatory: Boolean(metric.isMandatory),
            chartData: chartData,
          };
        });

        setSparcCardsData(transformedCards);
      }
    } catch (error) {
      // Use centralized handler to show friendly overlay/modal (same UX as GET /stand-up failures)
      console.error('Error fetching KPI metrics:', error);
      try {
        handleStandUpErrorWithFriendlyMessage(
          STANDUP_API_NAMES.FETCH_KPI_METRICS,
          error,
          true, // show modal/overlay to user
          { location: selectedLocation?.displayText, standupType, metricsType }
        );
      } catch (handlerError) {
        console.error('Error while handling KPI metrics error:', handlerError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on component mount and when dependencies change
  useEffect(() => {
    fetchKpiMetrics();
  }, [metricsType, standupId, selectedLocation?.displayText, standupType]);

  // Ensure all mandatory KPIs are auto-favorited and added to standup.
  useEffect(() => {
    // Avoid firing POST calls until favorites hydration completes,
    // so carried-forward favorites from last standup are respected.
    if (favoritesLoading) {
      return;
    }

    if (!sparcCardsData.length) {
      return;
    }

    const mandatoryCards = sparcCardsData.filter((card) => card.isMandatory);
    if (!mandatoryCards.length) {
      return;
    }

    const favoritesArray = Array.isArray(favoritesFromRedux)
      ? favoritesFromRedux
      : (favoritesFromRedux as any)?.data || [];

    favoritesArray.forEach((fav: any) => {
      if (fav?.item_type === 'kpi' && fav?.item_id && fav?.is_favourite === true) {
        mandatoryKpiSyncRegistry.delete(`${standupType}:${fav.item_id}`);
      }
    });

    const cardsToSync = mandatoryCards.filter((card) => {
      const registryKey = `${standupType}:${card.id}`;
      const isAlreadyFavorited = favoritesArray.some(
        (fav: any) => fav.item_type === 'kpi' && String(fav.item_id) === String(card.id) && fav.is_favourite === true
      );

      return !isAlreadyFavorited
        && !pendingMandatorySyncRef.current.has(card.id)
        && !mandatoryKpiSyncRegistry.has(registryKey);
    });

    if (!cardsToSync.length) {
      return;
    }

    const syncMandatoryFavorites = async () => {
      cardsToSync.forEach((card) => {
        const registryKey = `${standupType}:${card.id}`;
        pendingMandatorySyncRef.current.add(card.id);
        mandatoryKpiSyncRegistry.add(registryKey);

        // Keep local selected state aligned while API sync completes.
        if (!favoriteSparcCardsRef.current.includes(card.id)) {
          onToggleSparcFavoriteRef.current(card.id);
        }
      });

      const results = await Promise.allSettled(
        cardsToSync.map((card) => toggleFavorite(card.id, card.title, { skipRefresh: true }))
      );

      cardsToSync.forEach((card, index) => {
        const registryKey = `${standupType}:${card.id}`;
        pendingMandatorySyncRef.current.delete(card.id);
        const result = results[index];
        const syncSucceeded = result?.status === 'fulfilled' && result.value === true;
        if (!syncSucceeded) {
          mandatoryKpiSyncRegistry.delete(registryKey);
        }
      });

      const hasSuccessfulSync = results.some(
        (result) => result.status === 'fulfilled' && result.value === true
      );

      if (hasSuccessfulSync) {
        await refreshFavoritesList();
      }
    };

    syncMandatoryFavorites().catch((error) => {
      console.error('Error auto-favoriting mandatory KPI batch:', error);
      cardsToSync.forEach((card) => {
        pendingMandatorySyncRef.current.delete(card.title);
        mandatoryKpiSyncRegistry.delete(`${standupType}:${metricsType}:${card.title}`);
      });
    });
  }, [sparcCardsData, favoritesFromRedux, favoritesLoading, toggleFavorite, refreshFavoritesList, standupType, metricsType]);

  // Get all KPI cards with isFavorite property - exactly like allTasks in KeyActivitiesTab
  const allKpiCards = React.useMemo(() => {
    // Combine SPARC and Lighthouse cards
    const sparcWithFavorites = sparcCardsData.map(card => ({
      ...card,
      type: 'sparc',
      isFavorite: card.isMandatory || favoriteSparcCards.includes(card.id),
    }));

    const lighthouseWithFavorites = lighthouseCardsData.map(card => ({
      ...card,
      type: 'lighthouse',
      isFavorite: favoriteLighthouseCards.includes(card.id),
    }));

    return [...sparcWithFavorites, ...lighthouseWithFavorites];
  }, [sparcCardsData, lighthouseCardsData, favoriteSparcCards, favoriteLighthouseCards]);

  // Notify parent when allKpiCards changes - exactly like onAllTasksChange
  useEffect(() => {
    if (onAllCardsChange) {
      onAllCardsChange(allKpiCards);
    }
  }, [allKpiCards, onAllCardsChange]);

  // Handle period change
  const handlePeriodChange = (period: string) => {
    setMetricsType(period);
    if (onPeriodChange) {
      onPeriodChange(period);
    }
  };

  return (
    <View>
      {/* SPARC KPI LIST Section */}
      <SparcKpiListSection
        favoriteCards={favoriteSparcCards}
        allFavoriteCards={[...favoriteSparcCards, ...favoriteLighthouseCards]}
        onToggleFavorite={onToggleSparcFavorite}
        onToggleLighthouseFavorite={onToggleLighthouseFavorite}
        onCreateActionPlan={onSparcCardActionPlan}
        selectedPeriod={metricsType}
        onPeriodChange={handlePeriodChange}
        lighthouseCardsData={lighthouseCardsData}
        sparcCardsData={sparcCardsData}
        isLoading={isLoading}
      />

      {/* Lighthouse Execution Section */}
      <LighthouseExecutionSection
        favoriteCards={favoriteLighthouseCards}
        allFavoriteCards={[...favoriteSparcCards, ...favoriteLighthouseCards]}
        onToggleFavorite={onToggleLighthouseFavorite}
        selectedPeriod={selectedPeriod}
        onPeriodChange={onPeriodChange}
        lighthouseCardsData={lighthouseCardsData}
        selectedLocation={selectedLocation}
	      isLoading={isLoadingLighthouse}
        onCreateActionPlan={onLighthouseCardActionPlan}
        metricTrendData={metricTrendData}
      />
    </View>
  );
};
